import { Button } from "@/components/ui/button";
import { BookOpen, Lightbulb, ArrowRight, Building2 } from "lucide-react";
import type { CaseStudyReading } from "@/data/caseStudyReadings";

interface CaseStudyReadingStageProps {
  reading: CaseStudyReading;
  onContinue: () => void;
}

const CaseStudyReadingStage = ({ reading, onContinue }: CaseStudyReadingStageProps) => {
  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-background/80 backdrop-blur-md border-b border-primary/10">
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
            <Building2 className="w-4 h-4 text-primary" />
          </div>
          <div className="min-w-0">
            <p className="text-xs text-muted-foreground font-body uppercase tracking-wider">Case Study</p>
            <h1 className="font-heading font-bold text-sm text-foreground truncate">{reading.companyName}</h1>
          </div>
        </div>
      </div>

      <main className="max-w-5xl mx-auto px-4 py-8 pb-32 space-y-8">
        {/* Title Block */}
        <div className="space-y-3">
          <h2 className="font-heading font-bold text-2xl md:text-3xl text-foreground leading-tight">
            {reading.companyName}
          </h2>
          <p className="font-heading text-base md:text-lg text-primary/80 font-medium">
            {reading.subtitle}
          </p>
          <div className="h-px bg-gradient-to-r from-primary/30 via-primary/10 to-transparent" />
        </div>

        {/* Reading Section */}
        <div className="space-y-2">
          <div className="flex items-center gap-2 mb-4">
            <BookOpen className="w-4 h-4 text-muted-foreground" />
            <span className="text-xs font-heading uppercase tracking-wider text-muted-foreground">Strategic Briefing</span>
          </div>

          <div className="space-y-5">
            {reading.readingText.map((paragraph, i) => (
              <p
                key={i}
                className="font-body text-[15px] md:text-base leading-[1.8] text-foreground/85"
              >
                {paragraph}
              </p>
            ))}
          </div>
        </div>

        {/* Key Takeaway */}
        <div className="rounded-xl border border-accent/20 bg-accent/5 p-5 md:p-6 space-y-3">
          <div className="flex items-center gap-2">
            <Lightbulb className="w-4 h-4 text-accent" />
            <span className="font-heading font-semibold text-sm text-accent uppercase tracking-wider">Key Takeaway</span>
          </div>
          <p className="font-body text-[15px] leading-relaxed text-foreground/90">
            {reading.keyTakeaway}
          </p>
        </div>

        {/* Remember Points */}
        <div className="space-y-3">
          <p className="font-heading font-semibold text-sm text-muted-foreground uppercase tracking-wider">
            What to Remember
          </p>
          <ul className="space-y-2.5">
            {reading.rememberPoints.map((point, i) => (
              <li key={i} className="flex items-start gap-3">
                <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-primary/50 flex-shrink-0" />
                <span className="font-body text-sm leading-relaxed text-foreground/75">{point}</span>
              </li>
            ))}
          </ul>
        </div>
      </main>

      {/* Fixed Bottom CTA */}
      <div className="fixed bottom-0 left-0 right-0 bg-background/90 backdrop-blur-md border-t border-primary/10 p-4">
        <div className="max-w-5xl mx-auto">
          <Button
            onClick={onContinue}
            className="w-full py-3 bg-accent/15 border border-accent/30 text-accent font-heading font-semibold hover:bg-accent/25 shadow-[0_0_20px_hsl(var(--accent)/0.2)] gap-2"
          >
            Continue to Visual Breakdown
            <ArrowRight className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </div>
  );
};

export default CaseStudyReadingStage;
